// 참석자 대화 점유율 — 전사 구간 배열에서 화자별 '말한 시간'과 비율을 계산하는 순수 함수.
// 상세 머리 ⋮ 메뉴의 «참석자 대화 점유율» 창이 이 결과만 그린다(클로바 묶음 C3a, 2026-10-04).

// 🔴 `Number(null)`·`Number('')` 은 0 이라 «값이 없음» 이 0초로 섞인다 → 숫자·숫자 문자열만 변환하고
//    나머지는 NaN 으로 떨군 뒤 `Number.isFinite` 로 판정한다(`Math.max(0, NaN)` 은 NaN 이라 합계가 오염된다).
const num = (v) => {
  if (typeof v === 'number') return v;
  if (typeof v === 'string' && v.trim() !== '') return Number(v);
  return NaN;
};

/**
 * @param {Array} transcript 전사 구간 [{start, end, speaker, text}]
 * @returns {Array<{speaker: string|null, seconds: number, ratio: number}>}
 *   말한 시간 큰 순. `ratio` 는 0~1 비율(화면에서 정수 %로 반올림한다).
 *   · `start` 가 유한수가 아닌 구간은 뺀다(언제 말했는지 모른다).
 *   · `end` 가 유한수가 아니면 `start + 1` 로 본다(1초).
 *   · 길이는 `Math.max(0, end - start)` — 뒤집힌 구간은 0초.
 *   · 분모는 **모든 화자 seconds 의 합**(녹음 길이가 아니다 — 구간이 겹치면 녹음 길이를 넘을 수 있다).
 *   · 화자가 비면(`null`·`''`) `null` 한 묶음.
 *   · 0초인 화자(뒤집힌 구간뿐인 화자)는 결과에 넣지 않는다 — 창에 «0:00 · 0%» 행으로 남으면
 *     '말을 안 한 참석자'처럼 보인다. 0초는 분모 합에 영향이 없어 다른 화자 비율은 그대로다.
 *   · 합이 0초면 빈 배열.
 */
export function speakerShare(transcript) {
  const segs = Array.isArray(transcript) ? transcript : [];
  const totals = new Map();

  segs.forEach((s) => {
    if (!s || typeof s !== 'object') return;
    const start = num(s.start);
    if (!Number.isFinite(start)) return;
    const rawEnd = num(s.end);
    const end = Number.isFinite(rawEnd) ? rawEnd : start + 1;
    const seconds = Math.max(0, end - start);
    const speaker = s.speaker ? s.speaker : null;
    totals.set(speaker, (totals.get(speaker) || 0) + seconds);
  });

  const rows = Array.from(totals, ([speaker, seconds]) => ({ speaker, seconds }));
  const sum = rows.reduce((acc, row) => acc + row.seconds, 0);
  if (!(sum > 0)) return [];

  return rows
    .filter((row) => row.seconds > 0)
    .map((row) => ({ speaker: row.speaker, seconds: row.seconds, ratio: row.seconds / sum }))
    .sort((a, b) => b.seconds - a.seconds);
}

export default speakerShare;
